const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_LETTERS = 'JABCDEFGHI';
const CIF_NUMERIC_PREFIXES = 'ABEH';
const CIF_ALPHA_PREFIXES = 'KLMNPQRSW';

export const normalizeClientIdentification = (value: string): string =>
  value.trim().toUpperCase().replace(/[\s.-]/gu, '');

export function detectClientIdentificationCode(value: string): string | undefined {
  const document = normalizeClientIdentification(value);
  if (/^\d{8}[A-Z]$/u.test(document)) return '1';
  if (/^[XYZ]\d{7}[A-Z]$/u.test(document)) return '6';
  if (/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-Z]$/u.test(document)) return '9';
  return undefined;
}

function hasValidDni(document: string): boolean {
  return /^\d{8}[A-Z]$/u.test(document)
    && DNI_LETTERS[Number(document.slice(0, 8)) % 23] === document[8];
}

function hasValidNie(document: string): boolean {
  if (!/^[XYZ]\d{7}[A-Z]$/u.test(document)) return false;
  const number = `${'XYZ'.indexOf(document[0] ?? '')}${document.slice(1, 8)}`;
  return DNI_LETTERS[Number(number) % 23] === document[8];
}

function hasValidCif(document: string): boolean {
  if (!/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/u.test(document)) return false;
  let sum = 0;
  for (let index = 1; index <= 7; index += 1) {
    const digit = Number(document[index]);
    const doubled = digit * 2;
    sum += index % 2 === 0 ? digit : Math.floor(doubled / 10) + doubled % 10;
  }
  const control = (10 - sum % 10) % 10;
  const prefix = document[0] ?? '';
  if (CIF_NUMERIC_PREFIXES.includes(prefix)) return document[8] === String(control);
  if (CIF_ALPHA_PREFIXES.includes(prefix)) return document[8] === CIF_LETTERS[control];
  return document[8] === String(control) || document[8] === CIF_LETTERS[control];
}

export function clientIdentificationError(value: string, type: string): string | undefined {
  const document = normalizeClientIdentification(value);
  if (!document) return 'La identificación fiscal es obligatoria.';
  if (!/^[A-Z0-9]{1,16}$/u.test(document)) return 'Introduce un documento de identificación válido.';
  if (!/^\d{1,2}$/u.test(type)) return 'Selecciona el tipo de identificación de NET4.';
  const code = String(Number(type));
  const detected = detectClientIdentificationCode(document);
  const matchesNie = detected === '6' && ['3', '4', '6'].includes(code);
  if (detected && detected !== code && !matchesNie) {
    return 'El tipo de identificación no corresponde al documento introducido.';
  }
  if (code === '1') return hasValidDni(document) ? undefined : 'El DNI no es válido: revisa el número y la letra de control.';
  if (['3', '4', '6'].includes(code)) return hasValidNie(document) ? undefined : 'El NIE no es válido: revisa el número y la letra de control.';
  if (code === '9') return hasValidCif(document) ? undefined : 'El CIF no es válido: revisa el número y el carácter de control.';
  if (['2', '5'].includes(code)) return undefined;
  return 'Selecciona un tipo de identificación admitido por NET4.';
}
